import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquarePlus, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { PageLoader } from '../components/PageLoader';
import { InlineError } from '../components/InlineError';
import { whatItDoes } from '../lib/agents';
import { findSection } from '../lib/areas';
import { useAgentCard } from '../components/AgentDock';
import type { Agent } from '../lib/types';

type Skill = Agent & { areas: string[] };

// Every skill the agent can use, what each does, how to use it, and the business
// areas that suggest questions for it.
export default function SkillsPage() {
  const { authFetch } = useAuth();
  const { sections, agents } = useWorkspace();
  const { startChat } = useAgentCard();
  const [skills, setSkills] = useState<Skill[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const agentName = agents.find(a => a.default)?.name ?? 'Navaris';

  useEffect(() => {
    let stale = false;
    authFetch('/agents/skills')
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Could not load the skills')))
      .then((data: Skill[]) => { if (!stale) setSkills(data); })
      .catch(err => { if (!stale) { setError(err.message); setSkills([]); } });
    return () => { stale = true; };
  }, [authFetch]);

  if (skills === null) return <PageLoader label="Loading the skills..." />;

  return (
    <div>
      <div className="pb-6 mb-6 border-b border-softgrey">
        <h3 className="text-lg font-semibold">Skills</h3>
        <p className="text-sm text-coolslate mt-1">
          The skills {agentName} can use in a chat, and what each is for. Choose one from the
          chat, or start a message with its command, such
          as <code className="text-brand-dark">/{skills[0]?.key ?? 'pitch-deck-reviewer'}</code>.
          {' '}{agentName} also takes one up when a request calls for it.
        </p>
      </div>

      {error && <InlineError message={error} />}

      <div className="grid gap-3 md:grid-cols-2">
        {skills.map(skill => (
          <div key={skill.key} className="bg-white border border-softgrey rounded-xl p-4 flex flex-col">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-lg bg-offwhite text-brand-dark grid place-items-center shrink-0">
                <Sparkles className="w-4 h-4" />
              </span>
              <div className="min-w-0 flex-1">
                <h4 className="navaris-h3">{skill.name}</h4>
                <code className="block text-xs text-coolslate">/{skill.key}</code>
              </div>
            </div>

            <div className="mt-3 mb-4 space-y-2">
              {skill.description && (
                <p className="text-sm text-coolslate leading-snug">{whatItDoes(skill.description)}</p>
              )}
              {skill.areas.length > 0 && (
                <p className="text-xs text-coolslate">
                  Suggested in{' '}
                  {skill.areas.map((id, i) => (
                    <span key={id}>
                      {i > 0 && ', '}
                      <Link to={`/workspace/${id}`} className="text-brand-dark hover:underline">
                        {findSection(sections, id)?.name ?? id}
                      </Link>
                    </span>
                  ))}
                </p>
              )}
            </div>

            <div className="mt-auto pt-3 border-t border-softgrey/60">
              <button
                type="button"
                onClick={() => startChat(skill.key)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-dark hover:underline"
              >
                <MessageSquarePlus className="w-4 h-4" />
                Start a chat with this skill
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
